import { LitElement, html } from 'lit'
import { ModalSaveMixin } from '../core/mixins/modal-save.mixin.js'
import { responsavelCacheService } from '../core/services/responsavel-cache.service.js'

/**
 * Modal para criar e editar responsáveis
 * Responsáveis são pessoas associadas às transações
 * Trata diferentemente o responsável próprio ("Eu") e outros
 * Impede duplicação de email com usuário logado
 *
 * Utiliza ModalSaveMixin para:
 * - Loading state no botão Salvar com guard contra clique duplo
 * - Chamada direta ao responsavelCacheService (criar/atualizar)
 * - Tratamento de erro inline com mensagem truncada
 * - Guard no close() impedindo fechamento durante salvamento
 * - Eventos cache-operation-started/finished via window
 *
 * Layout: flexbox com header fixo, conteúdo com scroll independente e footer fixo
 * com botões de ação sempre visíveis (padrão 040-modal-scroll-layout-padronizado)
 */
class ModalResponsavel extends ModalSaveMixin(LitElement) {
  static properties = {
    isEditMode: { type: Boolean },
    responsavelData: { type: Object },
    currentUserEmail: { type: String }
  }

  /**
   * Identificador da entidade para eventos e data-cy de erro inline.
   * Usado pelo ModalSaveMixin para gerar data-cy="erro-salvar-responsavel"
   * e nos eventos cache-operation-started/finished.
   * @type {string}
   */
  _entidade = 'responsavel'

  createRenderRoot () {
    return this
  }

  constructor () {
    super()
    this.open = false
    this.isEditMode = false
    this.responsavelData = null
    this.originalTheme = null
    this.currentUserEmail = ''
  }

  /**
   * Fecha o modal restaurando o tema original e delegando ao ModalSaveMixin.
   * O mixin gerencia o guard contra _saving, reset de estado,
   * e delega ao ModalBaseMixin para open=false, disparo de evento modal-closed
   * e integração com History API.
   *
   * @param {object} [options={}] - Opções de fechamento repassadas ao mixin base
   * @param {boolean} [options.fromPopstate=false] - Indica se veio do popstate
   */
  close (options = {}) {
    if (this._saving) return

    if (this.originalTheme) {
      document.documentElement.setAttribute('data-theme', this.originalTheme)
      this.originalTheme = null
    }

    super.close(options)
  }

  /**
   * Abre o modal para criação ou edição de responsável.
   * Armazena tema original para restauração no close().
   * Preenche campos no setTimeout para garantir que o DOM está renderizado.
   *
   * @param {Object|null} responsavelData - Dados do responsável para edição (null = criação)
   * @param {boolean} isCurrentUser - Se o responsável é o próprio usuário logado
   * @param {string} currentUserEmail - Email do usuário logado para validação de duplicação
   */
  show (responsavelData = null, isCurrentUser = false, currentUserEmail = '') {
    this.isEditMode = !!responsavelData
    this.responsavelData = responsavelData
    this.isCurrentUser = isCurrentUser
    this.currentUserEmail = currentUserEmail

    this.originalTheme = document.documentElement.getAttribute('data-theme') || 'light'

    this.open = true

    if (this.isEditMode && responsavelData) {
      setTimeout(() => {
        const nameInput = this.renderRoot.querySelector('[data-cy="input-nome-responsavel"]')
        const emailInput = this.renderRoot.querySelector('[data-cy="input-email-responsavel"]')
        const phoneInput = this.renderRoot.querySelector('[data-cy="input-telefone-responsavel"]')
        const apelidoInput = this.renderRoot.querySelector('[data-cy="input-apelido-responsavel"]')

        if (nameInput) nameInput.value = responsavelData.nome
        if (emailInput) emailInput.value = responsavelData.email || ''
        if (phoneInput) phoneInput.value = responsavelData.telefone || ''
        if (apelidoInput) apelidoInput.value = responsavelData.apelido || 'Eu'
      }, 0)
    }
  }

  /**
   * Fecha o modal ao clicar no backdrop (área escurecida)
   * Delega para this.close() que já gerencia o guard contra _saving
   */
  _handleBackdropClick () {
    this.close()
  }

  _stopPropagation (e) {
    e.stopPropagation()
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Salvar/Atualizar
   * Replica o padrão do floating-button com transição suave de cor
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onSaveHover (e) {
    e.target.style.backgroundColor = 'var(--hover-primary, #7B3EF0)'
  }

  /**
   * Restaura cor original do botão Salvar/Atualizar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onSaveLeave (e) {
    e.target.style.backgroundColor = ''
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Cancelar
   * Usa background secundário para destacar o botão neutro
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onCancelHover (e) {
    e.target.style.backgroundColor = 'var(--glass-bg, rgba(117, 133, 255, 0.22))'
  }

  /**
   * Restaura cor original do botão Cancelar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onCancelLeave (e) {
    e.target.style.backgroundColor = ''
  }

  /**
   * Executa a operação de salvamento via ModalSaveMixin._executeSave.
   * Valida campos obrigatórios e impede duplicação de email com usuário logado.
   * Determina operação (criar/atualizar) com base no isEditMode.
   * Chama responsavelCacheService diretamente — não dispara evento legado.
   *
   * @returns {Promise<void>}
   */
  async _handleSave () {
    if (this._saving) return

    const nameInput = this.renderRoot.querySelector('[data-cy="input-nome-responsavel"]')
    const emailInput = this.renderRoot.querySelector('[data-cy="input-email-responsavel"]')
    const phoneInput = this.renderRoot.querySelector('[data-cy="input-telefone-responsavel"]')
    const apelidoInput = this.renderRoot.querySelector('[data-cy="input-apelido-responsavel"]')

    // Validação de campos obrigatórios conforme tipo de responsável
    if (this.isCurrentUser) {
      if (!apelidoInput?.value.trim()) {
        apelidoInput.focus()
        return
      }
    } else {
      if (!nameInput.value.trim()) {
        nameInput.focus()
        return
      }

      // Impede duplicação de email com usuário logado
      const email = emailInput.value.trim()
      if (email && email === this.currentUserEmail) {
        alert('Não é possível cadastrar um responsável com o mesmo e-mail do usuário logado.')
        emailInput.focus()
        return
      }
    }

    // Coleta dados do formulário
    const dados = {
      nome: nameInput.value.trim(),
      email: emailInput.value.trim() || null,
      telefone: phoneInput.value.trim() || null,
      apelido: apelidoInput?.value.trim() || null
    }

    // Determina operação e ID com base no modo
    const operacao = this.isEditMode ? 'atualizar' : 'criar'
    const id = this.isEditMode ? this.responsavelData?.id : undefined

    // Delega ao mixin que gerencia loading, timeout, erro e fechamento
    await this._executeSave(responsavelCacheService, operacao, dados, id)
  }

  render () {
    return html`
      <div 
        class="modal ${this.open ? 'is-active' : ''}" 
        @click="${this._handleBackdropClick}"
        style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 20000;
          display: ${this.open ? 'flex' : 'none'};
          align-items: center;
          justify-content: center;
        "
      >
        <div 
          class="card"
          @click="${this._stopPropagation}"
          style="
            width: 90%;
            max-width: 500px;
            max-height: 85vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border-radius: var(--radius-card, 20px);
            box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));
            position: relative;
          "
        >
          <!-- Zona 1: Header fixo no topo com sombra sobre o conteúdo -->
          <header class="card-header" style="flex-shrink: 0; position: relative; z-index: 2; box-shadow: var(--shadow-header-modal, 0 4px 12px rgba(0,0,0,0.3));">
            ${this.renderBotaoVoltarIOS()}
            <p class="card-header-title" data-cy="modal-responsavel-title">
              ${this.isEditMode ? 'Editar Responsável' : 'Adicionar Responsável'}
            </p>
            ${this.renderBotaoFechar('modal-responsavel-close')}
          </header>

          <!-- Zona 2: Conteúdo com scroll independente -->
          <div class="card-content custom-scrollbar" @input="${this._clearError}" @change="${this._clearError}" style="
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
            min-height: 0;
          ">
            ${this.isCurrentUser ? html`
              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Apelido *</label>
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    placeholder="Ex: Eu, Meu nome"
                    required
                    data-cy="input-apelido-responsavel"
                  />
                </div>
                <p class="help">Como você quer ser identificado nas transações</p>
              </div>
              
              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Nome</label>
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    readonly
                    disabled
                    data-cy="input-nome-responsavel"
                  />
                </div>
                <p class="help">Nome não pode ser alterado</p>
              </div>

              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">E-mail</label>
                <div class="control">
                  <input
                    class="input"
                    type="email"
                    readonly
                    disabled
                    data-cy="input-email-responsavel"
                  />
                </div>
                <p class="help">E-mail não pode ser alterado</p>
              </div>
            ` : html`
              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Nome *</label>
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    placeholder="Ex: João Silva"
                    required
                    data-cy="input-nome-responsavel"
                  />
                </div>
              </div>

              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">E-mail</label>
                <div class="control">
                  <input
                    class="input"
                    type="email"
                    placeholder="Ex: joao@email.com"
                    data-cy="input-email-responsavel"
                  />
                </div>
              </div>
            `}

            <div class="field mb-4">
              <label class="label is-size-7 has-text-weight-medium">Telefone</label>
              <div class="control">
                <input
                  class="input"
                  type="tel"
                  placeholder="Ex: (11) 99999-9999"
                  data-cy="input-telefone-responsavel"
                />
              </div>
            </div>
          </div>

          <!-- Erro inline de salvamento — posicionado entre conteúdo e footer -->
          ${this.renderErroSalvar()}

          <!-- Zona 3: Footer fixo com botões de ação e sombra sobre o conteúdo -->
          <footer class="card-footer"
            data-cy="footer-modal-responsavel"
            aria-label="Ações do modal de responsável"
            style="
              flex-shrink: 0;
              padding: 1rem 1.5rem;
              background-color: var(--bg-surface, #151C30);
              border-top: 1px solid var(--border, #26304A);
              position: relative;
              z-index: 2;
              box-shadow: var(--shadow-footer-modal, 0 -4px 12px rgba(0,0,0,0.3));
            "
          >
            <div class="field is-grouped is-justify-content-flex-end" style="width: 100%">
              <div class="control">
                <button 
                  class="button" 
                  type="button" 
                  data-cy="btn-cancelar-responsavel"
                  ?disabled="${this._saving}"
                  @click="${this.close}"
                  @mouseenter="${this._onCancelHover}"
                  @mouseleave="${this._onCancelLeave}"
                >
                  Cancelar
                </button>
              </div>
              <div class="control">
                <button 
                  class="button is-primary ${this._saving ? 'is-loading' : ''}" 
                  type="button" 
                  data-cy="btn-salvar-responsavel"
                  ?disabled="${this._saving}"
                  @click="${this._handleSave}"
                  @mouseenter="${this._onSaveHover}"
                  @mouseleave="${this._onSaveLeave}"
                >
                  ${this.isEditMode ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </div>
          </footer>
        </div>
      </div>
    `
  }
}

customElements.define('modal-responsavel', ModalResponsavel)
